import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { AnimationController, PopoverController } from '@ionic/angular';
import { isPlatformBrowser } from '@angular/common';
import { ComponentRef } from '@angular/core';
import { ScreenLayer } from '../enums/screen-layer.enum';
import { ModalDomain } from '../enums/modal-domain.enum';

export interface CustomPopoverOptions {
  component: any;
  componentProps?: { [key: string]: any };
  cssClass?: string | string[];
  backdropDismiss?: boolean;
  showBackdrop?: boolean;
  animated?: boolean;
  // Screen layer for z-index layering (layer * 1000)
  layer?: ScreenLayer;
  // Modal domain for dismiss/replace logic
  modalDomain?: ModalDomain;
  // Custom animation options
  animationDuration?: number;
  scaleFrom?: number;
  scaleTo?: number;
  opacityFrom?: number;
  opacityTo?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CustomPopoverService {
  private platformId = inject(PLATFORM_ID);
  private popoverCtrl = inject(PopoverController);
  private animationCtrl = inject(AnimationController);

  private currentPopover: HTMLIonPopoverElement | null = null;

  /**
   * Create and present a popover with custom scale + opacity animation
   */
  async create(opts: CustomPopoverOptions): Promise<HTMLIonPopoverElement> {
    // Default animation values
    const animationDuration = opts.animationDuration ?? 250;
    const scaleFrom = opts.scaleFrom ?? 0.85;
    const scaleTo = opts.scaleTo ?? 1;
    const opacityFrom = opts.opacityFrom ?? 1;
    const opacityTo = opts.opacityTo ?? 1;

    // Create the popover
    const popover = await this.popoverCtrl.create({
      component: opts.component,
      componentProps: opts.componentProps,
      cssClass: this.normalizeCssClass(opts.cssClass),
      backdropDismiss: opts.backdropDismiss ?? false,
      showBackdrop: opts.showBackdrop ?? true,
      animated: false, // We handle animation ourselves
    });

    // Dismiss existing popover if same layer + domain
    if (this.currentPopover) {
      const existingLayer = this.currentPopover.getAttribute('modal-layer');
      const existingDomain = this.currentPopover.getAttribute('modal-domain');
      if (existingLayer == opts.layer?.toString() && existingDomain == opts.modalDomain) {
        console.log('🎯 Dismissing existing popover - same layer + domain');
        await this.dismiss(null, 'another-popover-entrance');
      }
    }

    this.currentPopover = popover;

    // Store layer and domain as DOM attributes
    if (opts.layer !== undefined) {
      popover.setAttribute('modal-layer', opts.layer.toString());
    }
    if (opts.modalDomain) {
      popover.setAttribute('modal-domain', opts.modalDomain);
    }



    // Wait for popover to be added to DOM, then hide content before presenting
    // This follows the same pattern as CustomModalController
    const popoverContent = popover.shadowRoot?.querySelector('.popover-content') as HTMLElement;
    const backdrop = popover.shadowRoot?.querySelector('ion-backdrop') as HTMLElement;

    if (popoverContent) {
      // Hide content initially (same pattern as modal controller)
      popoverContent.style.opacity = '0';
      popoverContent.style.visibility = 'hidden';
      popoverContent.style.pointerEvents = 'none';
      popoverContent.style.transform = `scale(${scaleFrom})`;
    }

    if (backdrop) {
      backdrop.style.opacity = '0';
    }
    await popover.present();

    if (isPlatformBrowser(this.platformId)) {

      // Use requestAnimationFrame to ensure styles are applied before animating
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => {
          // Show and animate
          if (popoverContent) {
            popoverContent.style.visibility = 'visible';
            popoverContent.style.pointerEvents = 'auto';
          }

          this.animateIn(popover, {
            duration: animationDuration,
            scaleFrom,
            scaleTo,
            opacityFrom,
            opacityTo
          }).then(() => resolve());
        });
      });
    }

    return popover;
  }

  /**
   * Dismiss the current popover with exit animation
   */
  async dismiss(data?: any, role?: string): Promise<boolean> {
    if (!this.currentPopover) {
      return false;
    }

    // Apply exit animation
    if (isPlatformBrowser(this.platformId)) {
      await this.animateOut(this.currentPopover);
    }

    const result = await this.currentPopover.dismiss(data, role);
    this.currentPopover = null;
    return result;
  }

  /**
   * Custom entry animation with scale and opacity
   */
  private async animateIn(
    popover: HTMLIonPopoverElement,
    opts: { duration: number; scaleFrom: number; scaleTo: number; opacityFrom: number; opacityTo: number }
  ): Promise<void> {
    const popoverContent = popover.shadowRoot?.querySelector('.popover-content') as HTMLElement;
    const backdrop = popover.shadowRoot?.querySelector('ion-backdrop') as HTMLElement;

    if (!popoverContent) return;

    // Animate content
    const contentAnimation = this.animationCtrl.create('popover-content-enter')
      .addElement(popoverContent)
      .duration(opts.duration)
    //   .easing('cubic-bezier(.8,1.4,.47,.9)')
      .easing('cubic-bezier(.44,1.53,.16,.86)')
    //   .easing('cubic-bezier(0,1.13,.3,1.06)')
    //   .easing('cubic-bezier(0.1, 0.92, 0, 1)')
      .fromTo('opacity', opts.opacityFrom, opts.opacityTo)
      .fromTo('scale', `${opts.scaleFrom}`, `${opts.scaleTo}`);
    //   .fromTo('transform', `scale(${opts.scaleFrom})`, `scale(${opts.scaleTo})`);

    // Animate backdrop if present
    if (backdrop) {
      const backdropAnimation = this.animationCtrl.create('popover-backdrop-enter')
        .addElement(backdrop)
        .duration(opts.duration)
        .easing('ease-out')
        .fromTo('opacity', '0', '1');

      await Promise.all([contentAnimation.play(), backdropAnimation.play()]);
    } else {
      await contentAnimation.play();
    }
  }

  /**
   * Custom exit animation with scale and opacity
   */
  private async animateOut(popover: HTMLIonPopoverElement): Promise<void> {
    const popoverContent = popover.shadowRoot?.querySelector('.popover-content') as HTMLElement;
    const backdrop = popover.shadowRoot?.querySelector('ion-backdrop') as HTMLElement;

    if (!popoverContent) return;

    const duration = 200;

    // Animate content out
    const contentAnimation = this.animationCtrl.create('popover-content-exit')
      .addElement(popoverContent)
      .duration(duration)
      .easing('cubic-bezier(0.32, 0.72, 0, 1)')
      .fromTo('opacity', '1', '0')
      .fromTo('transform', 'scale(1)', 'scale(0.9)');

    // Animate backdrop out if present
    if (backdrop) {
      const backdropAnimation = this.animationCtrl.create('popover-backdrop-exit')
        .addElement(backdrop)
        .duration(duration)
        .easing('ease-out')
        .fromTo('opacity', '1', '0');

      await Promise.all([contentAnimation.play(), backdropAnimation.play()]);
    } else {
      await contentAnimation.play();
    }
  }

  /**
   * Normalize CSS class to array format
   */
  private normalizeCssClass(cssClass?: string | string[]): string[] {
    if (!cssClass) return [];
    if (typeof cssClass === 'string') return [cssClass];
    return cssClass;
  }

  /**
   * Get the current popover element
   */
  getTop(): HTMLIonPopoverElement | null {
    return this.currentPopover;
  }
}
